import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Shield, ArrowLeft, Loader2, AlertTriangle, ExternalLink } from 'lucide-react';
import api from '@/lib/api';
import { Alert, AlertDescription } from '@/components/ui/alert';
import TenantFooter from '@/components/TenantFooter';
import { sanitizeHtml } from '@/lib/sanitizeHtml';
import { usePageTitle } from '@/hooks/usePageTitle';

/**
 * Public policy page that renders either the pasted content or an external
 * link for a tenant's Política de Privacidade / Termos de Utilização.
 *
 * Route: /privacidade/:tenantSlug (kind="privacy") or /termos/:tenantSlug (kind="terms")
 */
const PolicyPage = ({ kind }) => {
  const { tenantSlug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [branding, setBranding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const pageLabel = kind === 'privacy' ? 'Política de Privacidade' : 'Termos de Utilização';
  usePageTitle(branding?.name, pageLabel);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [{ data: policy }, { data: b }] = await Promise.all([
          api.get(`/public/tenant/${tenantSlug}/policy/${kind}`),
          api.get(`/public/tenant/${tenantSlug}/branding`),
        ]);
        if (cancelled) return;
        setData(policy);
        setBranding(b);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.detail || 'Erro ao carregar documento');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tenantSlug, kind]);

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate(`/${tenantSlug}`)}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
            data-testid="policy-back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar
          </button>
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-indigo-700" />
            <span className="text-sm font-semibold text-slate-900">CogniEtica</span>
            {branding?.name && <span className="text-sm text-slate-500">— {branding.name}</span>}
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-4xl w-full mx-auto px-6 py-10">
        {loading && (
          <div className="flex items-center gap-2 text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin" />
            A carregar…
          </div>
        )}
        {error && (
          <Alert className="bg-red-50 border-red-200">
            <AlertTriangle className="w-4 h-4 text-red-600" />
            <AlertDescription className="text-red-800">{error}</AlertDescription>
          </Alert>
        )}
        {!loading && !error && data && (
          <>
            <h1 className="text-3xl font-semibold text-slate-900 mb-2" data-testid="policy-title">
              {data.title}
            </h1>
            <p className="text-sm text-slate-500 mb-8">
              {data.tenant_name}
            </p>

            {data.url && !data.content && (
              <div className="bg-white border border-slate-200 rounded-lg p-6 text-center">
                <p className="text-slate-700 mb-4">
                  Este documento está disponível externamente.
                </p>
                <a
                  href={data.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-indigo-700 hover:text-indigo-900 font-medium"
                  data-testid="policy-external-link"
                >
                  Abrir documento <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            )}

            {data.content && (
              <article
                className="prose prose-slate max-w-none bg-white border border-slate-200 rounded-lg p-8"
                data-testid="policy-content"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(data.content) }}
              />
            )}

            {data.url && data.content && (
              <div className="mt-6 text-sm">
                <a
                  href={data.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-700 hover:text-indigo-900 inline-flex items-center gap-1"
                >
                  Consultar versão externa <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            )}

            <div className="mt-8">
              <Link
                to={`/${tenantSlug}`}
                className="text-sm text-slate-500 hover:text-slate-800"
              >
                &larr; Voltar ao canal de denúncias
              </Link>
            </div>
          </>
        )}
      </main>

      <TenantFooter branding={branding} />
    </div>
  );
};

export default PolicyPage;
